import { useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { formatMoney } from "../lib/format";
import { useBackButton } from "../lib/telegram";
import { BalancesOut } from "../lib/schemas";
import { Avatar, BalanceAmount, Skeleton } from "../ui";

export default function Balances() {
  const nav = useNavigate();
  const { id } = useParams<{ id: string }>();
  const groupId = Number(id);

  const { data, isLoading } = useQuery({
    queryKey: ["balances", groupId],
    queryFn: () => api<BalancesOut>("GET", `/chats/${groupId}/balances`),
  });

  const handleBack = useCallback(() => nav(`/groups/${groupId}`), [nav, groupId]);
  useBackButton(handleBack);

  if (isLoading || !data) {
    return (
      <div className="min-h-screen bg-bg px-4 pt-6 pb-8">
        <Skeleton className="h-4 w-28 mx-auto mb-6" />
        <div className="bg-surface border border-hairline rounded-2xl shadow-card overflow-hidden">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="flex items-center justify-between px-4 py-3.5 border-t border-hairline first:border-t-0">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-16" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg px-4 pt-6 pb-8">
      <h1 className="font-sans text-base font-semibold text-fg mb-6 text-center">
        Balances
      </h1>

      {/* Settlement plan */}
      <h2 className="font-sans text-xs font-medium text-muted mb-2.5 px-1">
        Settlement plan
      </h2>
      {data.plan.length === 0 ? (
        <p className="text-muted text-sm text-center py-4 mb-6">
          Everyone is settled up!
        </p>
      ) : (
        <div className="bg-surface border border-hairline rounded-2xl shadow-card overflow-hidden mb-6">
          {data.plan.map((transfer, i) => (
            <div
              key={i}
              className="flex items-center justify-between px-4 py-3.5 border-t border-hairline first:border-t-0"
            >
              <span className="text-fg text-sm">
                {transfer.from_user.first_name}
                <span className="text-muted mx-2">→</span>
                {transfer.to_user.first_name}
              </span>
              <span className="text-fg font-semibold font-mono text-sm [font-variant-numeric:tabular-nums]">
                {formatMoney(transfer.amount, data.currency)}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Per-member balances */}
      <h2 className="font-sans text-xs font-medium text-muted mb-2.5 px-1">
        Per-member balances
      </h2>
      {data.rows.length === 0 ? (
        <p className="text-muted text-sm text-center py-4">No members.</p>
      ) : (
        <div className="bg-surface border border-hairline rounded-2xl shadow-card overflow-hidden">
          {data.rows.map((row) => (
            <div
              key={row.user.id}
              className="flex items-center gap-3 px-4 py-3.5 border-t border-hairline first:border-t-0"
            >
              <Avatar name={row.user.first_name} size="md" />
              <span className="text-fg text-sm flex-1 min-w-0 truncate">{row.user.first_name}</span>
              <BalanceAmount size="sm" balance={row.net} currency={data.currency} zeroLabel="±0.00" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
